import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, BookOpen, Users, MessageSquare, Zap, CheckCircle2, Building2 } from "lucide-react";

const offerings = [
  {
    icon: BookOpen,
    title: "Корпоративный курс",
    description:
      "Разрабатываем программу обучения под задачи вашей команды: от основ ClickHouse до продвинутой оптимизации запросов и проектирования схем данных.",
    points: [
      "Адаптация под стек и уровень команды",
      "Практические задания на ваших данных",
      "Доступ к платформе на весь срок обучения",
      "Итоговая проверка знаний",
    ],
  },
  {
    icon: Users,
    title: "Воркшопы и сессии",
    description:
      "Интенсивные практические сессии для небольших групп (3–12 человек). Разбираем реальные кейсы, оптимизируем конкретные запросы и схемы из вашей системы.",
    points: [
      "Онлайн или офлайн формат",
      "От 2 часов до 2 дней",
      "Разбор ваших production-запросов",
      "Запись сессии для команды",
    ],
  },
  {
    icon: MessageSquare,
    title: "Консультации",
    description:
      "Точечные консультации по архитектуре, производительности и выбору подходов. Подходит для команд, у которых уже есть система и конкретные вопросы.",
    points: [
      "Аудит схем и запросов",
      "Рекомендации по партиционированию и индексам",
      "Проектирование под нагрузку 100M+ строк",
      "Помощь с миграцией на ClickHouse",
    ],
  },
];

const useCases = [
  { label: "Аналитические команды", description: "Переходящие на ClickHouse с других OLAP-систем" },
  { label: "Data Engineering", description: "Проектирование пайплайнов и ETL-процессов" },
  { label: "Product Analytics", description: "Построение BI-слоя и продуктовых метрик" },
  { label: "DevOps / Platform", description: "Развёртывание и администрирование кластеров" },
];

export const CorporatePage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-full bg-neutral-light overflow-y-auto">
      {/* Hero */}
      <div className="relative bg-neutral-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-teal-400/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
          <Link
            to="/courses"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Все курсы
          </Link>

          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-teal-400/20 text-teal-300 border border-teal-400/30">
              <Building2 className="h-3 w-3" />
              B2B
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-snug">
            Корпоративное обучение
          </h1>
          <p className="text-white/65 text-base leading-relaxed max-w-xl">
            Помогаем командам освоить ClickHouse быстро и без потерь в продуктивности. Курсы, воркшопы и консультации — под конкретные задачи вашего бизнеса.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">

        {/* Offerings */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-neutral-text/40 uppercase tracking-wider">
            Форматы работы
          </h2>
          {offerings.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-2xl p-6 border border-neutral-border shadow-sm"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center border border-teal-100">
                  <item.icon className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-text">{item.title}</h3>
                  <p className="text-sm text-neutral-text/65 mt-0.5 leading-relaxed">{item.description}</p>
                </div>
              </div>
              <ul className="grid sm:grid-cols-2 gap-2 mt-4">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-neutral-text/70 leading-snug">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Who it's for */}
        <section className="bg-white rounded-2xl p-6 border border-neutral-border shadow-sm">
          <h2 className="text-xs font-semibold text-neutral-text/40 uppercase tracking-wider mb-4">
            Кому подходит
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {useCases.map((uc) => (
              <div key={uc.label} className="flex items-start gap-2.5">
                <Zap className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-neutral-text leading-none mb-0.5">{uc.label}</p>
                  <p className="text-xs text-neutral-text/55">{uc.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Expertise note */}
        <p className="text-xs text-neutral-text/40 italic px-1">
          Опыт построен на production-системах с нагрузкой от 10 млрд событий в сутки. Мы не читаем по слайдам — разбираем реальные задачи.
        </p>

        {/* CTA */}
        <div className="bg-neutral-800 rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`,
              backgroundSize: "20px 20px",
            }}
          />
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-teal-400/15 blur-2xl pointer-events-none" />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-white mb-1">Обсудить формат</p>
              <p className="text-sm text-white/55 leading-relaxed max-w-sm">
                Расскажите о задаче — предложим программу и формат под вашу команду.
              </p>
            </div>
            <a
              href="https://t.me/quantile_camp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold transition-colors shadow-sm"
            >
              Написать в Telegram
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};
